/**
 * The one rule for which characters a person's name is made of.
 *
 * DOM-free by design: the keydown/paste/input handler that enforces this in a
 * field belongs to the app, because only the app has an `<input>`. What every
 * app shares — and must not fork — is the character rule and the folds below.
 */
/**
 * A name is letters and combining marks from any script (Ana Sofía, Müller,
 * प्रिया), spaces, hyphens and apostrophes: O'Brien and Mary-Jane are spellings,
 * not typos, and a field that drops those characters corrupts the name the
 * customer came here to give.
 *
 * `\p{M}` is in the class because a combining mark is a letter of the spelling,
 * not a decoration on one: a Devanagari or Tamil vowel sign is `\p{Mc}`/`\p{Mn}`
 * and the word cannot be spelled without it, and the decomposed form that macOS
 * pastes, several mobile keyboards and PDF copy all produce writes José as "e"
 * followed by U+0301. A rule without it stores "Jose" for a customer named José
 * and "परय" for one named प्रिया, in the product whose primary data is names.
 *
 * The class is written once and both the predicate and the sanitizer are built
 * from it, so the character a field admits is exactly the character a paste
 * keeps.
 */
const NAME_CLASS = String.raw `\p{L}\p{M} '\-`;
/**
 * Whether a character belongs in a name, judged on the folded, canonical form.
 *
 * No `g` flag: a shared regex with `lastIndex` state gives alternating answers
 * to `.test()`.
 */
export const NAME_CHARACTER = new RegExp(`^[${NAME_CLASS}]$`, "u");
const NON_NAME_CHARACTERS = new RegExp(`[^${NAME_CLASS}]+`, "gu");
/**
 * The apostrophe, however it was typed: iOS and macOS smart punctuation emit
 * U+2019 (and U+2018 where the quote was read as opening), several keyboards
 * emit the modifier letter U+02BC, and a CJK IME emits the fullwidth U+FF07.
 * Folding — rather than also accepting them — keeps one person's name one
 * string wherever it is stored, compared or searched. U+02BC is `\p{Lm}`, so
 * without the fold it passes the letter rule untouched and O'Brien survives as
 * two different strings that look identical.
 */
const APOSTROPHE_VARIANTS = /[\u2018\u2019\u02BC\uFF07]/g;
/**
 * The space, however it arrived: every space separator (`\p{Zs}` — the
 * non-breaking space above all, which is the commonest invisible character in
 * pasted text) plus the tab and line breaks a multi-line paste carries.
 *
 * A separator is folded and never dropped. Dropping the non-breaking space in
 * "Jean Luc" gives "JeanLuc", which is a different name — categorically worse
 * than dropping a decoration. Zero-width characters are not separators and are
 * not here: they are invisible, they belong to no spelling, and the class strips
 * them.
 */
const SPACE_VARIANTS = /[\p{Zs}\t\n\v\f\r\u2028\u2029]/gu;
/**
 * The hyphen, however it arrived: the typographic hyphen U+2010, the
 * non-breaking hyphen U+2011, the figure dash U+2012 and the en dash U+2013 all
 * reach a field from word processors and PDFs, and the fullwidth U+FF0D from a
 * CJK IME. Each separates the halves of Mary-Jane, so each folds onto the
 * hyphen rather than being dropped. The em dash is not here: it is a mark of
 * punctuation between clauses, not a hyphen inside a name.
 */
const HYPHEN_VARIANTS = /[\u2010-\u2013\uFF0D]/g;
/** One codepoint, astral characters included, line terminators counted. */
const ONE_CHARACTER = /^.$/su;
/**
 * Keeps only the characters a name is made of, in one canonical spelling.
 *
 * Nullish and empty input give `""`, so a field can pipe an optional value
 * straight through.
 */
export function toNameText(raw) {
    if (!raw)
        return "";
    // NFC first, so the composed and decomposed spellings of one name are one
    // string before anything judges it: "José" typed on Windows and "José" pasted
    // from a Mac are the same customer, and a matching product that stores both
    // byte sequences has two people where there is one. NFC keeps the accent — it
    // chooses how the accent is written, which is what the apostrophe fold does
    // for the apostrophe.
    return raw
        .normalize("NFC")
        .replace(APOSTROPHE_VARIANTS, "'")
        .replace(SPACE_VARIANTS, " ")
        .replace(HYPHEN_VARIANTS, "-")
        .replace(NON_NAME_CHARACTERS, "");
}
/**
 * Whether a single character may appear in a name, judged after the folds — so
 * U+2019 answers `true`, and a handler that admits a keystroke on this answer
 * inserts `toNameText(ch)` rather than the raw key, which is what puts the
 * folded apostrophe in the field.
 *
 * The answer comes from `toNameText` itself, so the keystroke gate and the
 * paste sanitizer cannot reach different verdicts about a character. One
 * keystroke can normalize to more than one codepoint — U+0958 is canonically a
 * letter plus a nukta — so the question this asks is whether anything survives,
 * not whether one character does.
 *
 * Anything that is not exactly one character — nullish, an empty string, a key
 * name like "Backspace" — answers `false`: the editing keys a field must let
 * through are the app's list, not a character rule.
 */
export function isNameChar(ch) {
    if (!ch || !ONE_CHARACTER.test(ch))
        return false;
    return toNameText(ch) !== "";
}
